if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get productId() {
        return this.dataset.productId;
      }

      get select() {
        return this.querySelector('select[name="id"]');
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      connectedCallback() {
        this.cleanup();

        document.body.classList.add('sticky-atc-bar-enabled');

        this.container = this.closest('.sticky-atc-bar');
        this.submitButton = this.querySelector('[name="add"]');
        this.hasLaunch = this.dataset.hasLaunch === 'true';
        this.pendingSelection = false;
        this.quantityRevision = 0;
        this.observeTarget = null;

        const source = this.container?.querySelector(
          'script[type="application/json"]'
        );

        try {
          this.variantData = JSON.parse(source?.textContent || '[]');
        } catch (error) {
          this.variantData = [];
          console.warn('Impossibile leggere le varianti della barra.', error);
        }

        this.selectedVariantId = this.select?.value || '';
        this.currentVariant = this.findVariant(this.selectedVariantId);
        this.findMainProductInfo();

        this.handleSelectChange = () => this.onSelectChange();
        this.select?.addEventListener('change', this.handleSelectChange);

        /*
         * Intercetta anche l'invio da tastiera.
         * Per i lanci il modulo sticky non acquista autonomamente.
         */
        this.boundForm = this.productForm;
        this.handleSubmit = (event) => this.onSubmit(event);
        this.boundForm?.addEventListener(
          'submit',
          this.handleSubmit,
          true
        );

        this.handlePhaseChange = (event) => {
          if (this.mainProductInfo?.contains(event.target)) {
            this.syncLaunchState();
          }
        };

        document.addEventListener(
          'launch:phase-change',
          this.handlePhaseChange
        );

        this.unsubscribeVariant = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          (event) => this.onMainVariantChange(event)
        );

        this.initObserver();

        if (this.hasLaunch) {
          this.syncLaunchState();
          this.launchSyncTimer = window.setInterval(
            () => this.syncLaunchState(),
            250
          );
        }
      }

      disconnectedCallback() {
        this.cleanup();

        if (!document.querySelector('sticky-atc-bar')) {
          document.body.classList.remove('sticky-atc-bar-enabled');
          document.documentElement.style.removeProperty(
            '--sticky-atc-bar-height'
          );
        }
      }

      cleanup() {
        window.clearInterval(this.launchSyncTimer);
        this.launchSyncTimer = null;

        this.observer?.disconnect();
        this.resizeObserver?.disconnect();
        this.quantityController?.abort();

        this.select?.removeEventListener(
          'change',
          this.handleSelectChange
        );

        this.boundForm?.removeEventListener(
          'submit',
          this.handleSubmit,
          true
        );

        document.removeEventListener(
          'launch:phase-change',
          this.handlePhaseChange
        );

        this.mediaQuery?.removeEventListener(
          'change',
          this.handleDeviceChange
        );

        this.unsubscribeVariant?.();
        this.unsubscribeVariant = null;
      }

      findVariant(id) {
        return this.variantData.find(
          (variant) => String(variant.id) === String(id)
        );
      }

      findMainProductInfo() {
        this.mainProductInfo = Array.from(
          document.querySelectorAll('product-info')
        ).find((element) => {
          return (
            String(element.dataset.productId) === String(this.productId) &&
            element.dataset.viewMode !== 'quick-view'
          );
        }) || null;

        this.productFormActions = this.mainProductInfo?.querySelector(
          '.main-product-form'
        ) || null;

        this.mainLaunchView = this.mainProductInfo?.querySelector(
          'launch-phase-view[data-product-id]'
        ) || null;
      }

      initObserver() {
        this.mediaQuery = window.matchMedia(
          FoxTheme.config.mediaQueryMobile
        );

        this.handleDeviceChange = () => this.checkDevice();
        this.mediaQuery.addEventListener(
          'change',
          this.handleDeviceChange
        );

        this.resizeObserver = new ResizeObserver(
          () => this.checkDevice()
        );
        this.resizeObserver.observe(this);
        this.checkDevice();

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.target !== this.observeTarget) return;

              this.container?.classList.toggle(
                'sticky-atc-bar--show',
                entry.intersectionRatio !== 1
              );
            });
          },
          {
            threshold: 1,
            rootMargin: '-80px 0px 0px 0px',
          }
        );

        if (!this.hasLaunch) {
          this.setObserveTarget(this.productFormActions);

          if (!this.productFormActions) {
            this.container?.classList.add('sticky-atc-bar--show');
          }
        }
      }

      setObserveTarget(target) {
        if (target === this.observeTarget) return;

        this.observer?.disconnect();
        this.observeTarget = target || null;

        if (target) this.observer?.observe(target);
      }

      checkDevice() {
        document.documentElement.style.setProperty(
          '--sticky-atc-bar-height',
          `${this.clientHeight}px`
        );
      }

      setPanelVisible(panel, visible) {
        panel.hidden = !visible;

        if (visible) {
          panel.style.removeProperty('display');
          panel.removeAttribute('inert');
          panel.removeAttribute('aria-hidden');
        } else {
          panel.style.setProperty('display', 'none', 'important');
          panel.setAttribute('inert', '');
          panel.setAttribute('aria-hidden', 'true');
        }
      }

      setSubmitState(disabled, text) {
        if (!this.submitButton) return;

        this.submitButton.disabled = Boolean(disabled);

        const label = this.submitButton.querySelector('span');

        if (label && text && label.textContent !== text) {
          label.textContent = text;
        }
      }

      syncLaunchState() {
        if (!this.hasLaunch || !this.container) return;

        this.findMainProductInfo();

        const phase = this.mainLaunchView?.getAttribute(
          'data-visible-phase'
        );

        this.dataset.waitlist = String(phase === 'lista_attesa');
        this.dataset.launchClosed = String(phase === 'chiuso');

        this.querySelectorAll('[data-sticky-launch-phase]').forEach(
          (panel) => {
            this.setPanelVisible(
              panel,
              panel.dataset.stickyLaunchPhase === phase
            );
          }
        );

        const quantityPanel = this.querySelector(
          `[id="QuantitySticky-${this.dataset.section}"]`
        );

        if (quantityPanel) {
          this.setPanelVisible(quantityPanel, phase === 'in_vendita');
        }

        const mainPanel = this.mainLaunchView?.querySelector(
          `[data-launch-phase-panel="${phase}"]`
        );

        const actionSelector = {
          lista_attesa: '.product-waitlist',
          in_vendita: '.main-product-form',
          chiuso: '.product-launch-closed',
        }[phase];

        const actionTarget = actionSelector
          ? mainPanel?.querySelector(actionSelector)
          : null;

        this.setObserveTarget(actionTarget);

        if (!actionTarget) {
          this.container.classList.remove('sticky-atc-bar--show');
        }

        const mainInput = this.productFormActions?.querySelector(
          '[name="id"]'
        );
        const mainButton = this.productFormActions?.querySelector(
          '[name="add"]'
        );

        if (mainInput?.value && !this.pendingSelection) {
          if (String(this.select?.value) !== String(mainInput.value)) {
            this.selectedVariantId = String(mainInput.value);
            if (this.select) this.select.value = this.selectedVariantId;
            this.currentVariant = this.findVariant(this.selectedVariantId);
            this.updatePrice();
            this.syncQuantityFromMain(true);
          }
        }

        const disabled =
          phase !== 'in_vendita' ||
          this.pendingSelection ||
          Boolean(this.mainProductInfo?.productInfoRequestPending) ||
          !mainButton ||
          mainButton.disabled ||
          !mainInput ||
          mainInput.disabled ||
          String(mainInput.value) !== String(this.select?.value);

        this.setSubmitState(
          disabled,
          disabled
            ? this.dataset.openingPendingLabel
            : mainButton?.querySelector('span')?.textContent
        );

        this.checkDevice();
      }

      onSelectChange() {
        this.findMainProductInfo();

        const id = this.select?.value;
        const variant = this.findVariant(id);

        if (!variant) {
          this.setSubmitState(true);
          return;
        }

        this.selectedVariantId = String(id);
        this.currentVariant = variant;
        this.updatePrice();

        const variantSelects = this.mainProductInfo?.querySelector(
          'variant-selects'
        );

        if (!variantSelects) {
          this.setSubmitState(
            this.hasLaunch || !variant.available,
            this.hasLaunch
              ? this.dataset.openingPendingLabel
              : (
                variant.available
                  ? FoxTheme.variantStrings.addToCart
                  : FoxTheme.variantStrings.soldOut
              )
          );
          return;
        }

        this.pendingSelection = true;
        this.setSubmitState(
          true,
          this.hasLaunch ? this.dataset.openingPendingLabel : null
        );

                let eventControl = null;

        Array.from(
          variantSelects.querySelectorAll('select, fieldset')
        ).forEach((element, index) => {
          const value = variant.options[index];

          if (element.tagName === 'SELECT') {
            const previousValue = element.value;

            Array.from(element.options).forEach((option) => {
              const selected = option.value === value;

              option.selected = selected;
              option.toggleAttribute('selected', selected);
            });

            element.value = value;

            if (previousValue !== value || !eventControl) {
              eventControl = element;
            }
          } else {
            element.querySelectorAll('input[type="radio"]').forEach(
              (radio) => {
                const wasChecked = radio.checked;
                radio.checked = radio.value === value;

                if (
                  radio.checked &&
                  (!wasChecked || !eventControl)
                ) {
                  eventControl = radio;
                }
              }
            );
          }
        });

        if (eventControl) {
          eventControl.dispatchEvent(
            new Event('change', { bubbles: true })
          );
        } else {
          this.setSubmitState(true);
        }

      }

      onMainVariantChange(event) {
        this.findMainProductInfo();

        if (
          !this.mainProductInfo ||
          event.data.sectionId !== this.mainProductInfo.sectionId
        ) {
          return;
        }

        const variant = event.data.variant;

        if (!variant) {
          this.setSubmitState(true, FoxTheme.variantStrings.unavailable);
          return;
        }

        /*
         * Un aggiornamento di una misura diversa da quella
         * richiesta non sblocca una selezione ancora pendente.
         */
        if (
          this.pendingSelection &&
          String(variant.id) !== String(this.selectedVariantId)
        ) {
          return;
        }

        this.pendingSelection = false;
        this.currentVariant = variant;
        this.selectedVariantId = String(variant.id);

        if (this.select) this.select.value = this.selectedVariantId;

        this.updatePrice();

        if (this.hasLaunch) {
          this.syncQuantityFromMain(true);
          this.syncLaunchState();
        } else {
          this.setSubmitState(
            !variant.available,
            variant.available
              ? FoxTheme.variantStrings.addToCart
              : FoxTheme.variantStrings.soldOut
          );
          this.updateQuantityInput();
        }
      }

      syncQuantityFromMain(copyValue = false) {
        const mainInput = this.productFormActions?.querySelector(
          '[name="quantity"]'
        );
        const stickyInput = this.quantityInput;

        if (!mainInput || !stickyInput) return;

        [
          'min',
          'max',
          'step',
          'data-min',
          'data-max',
          'data-cart-quantity',
          'data-quantity-variant-id',
        ].forEach((name) => {
          if (mainInput.hasAttribute(name)) {
            stickyInput.setAttribute(name, mainInput.getAttribute(name));
          } else {
            stickyInput.removeAttribute(name);
          }
        });

        if (copyValue) stickyInput.value = mainInput.value;
      }

      async updateQuantityInput() {
        const id = this.selectedVariantId;
        if (!id) return;

        this.quantityController?.abort();

        const controller = new AbortController();
        this.quantityController = controller;
        const revision = ++this.quantityRevision;

        try {
          const url = new URL(this.dataset.url, window.location.origin);
          url.searchParams.set('variant', id);
          url.searchParams.set('section_id', this.sectionId);

          const response = await fetch(url.toString(), {
            cache: 'no-store',
            signal: controller.signal,
          });

          if (!response.ok) throw new Error('Risposta quantità non valida');

          const text = await response.text();

          if (
            controller.signal.aborted ||
            !this.isConnected ||
            revision !== this.quantityRevision ||
            String(this.selectedVariantId) !== String(id)
          ) {
            return;
          }

          const html = new DOMParser().parseFromString(text, 'text/html');
          const selector = `[id="QuantitySticky-${this.sectionId}"]`;
          const source = html.querySelector(selector);
          const destination = this.querySelector(selector);

          if (source && destination) {
            destination.innerHTML = source.innerHTML;
          }
        } catch (error) {
          if (error.name !== 'AbortError') console.error(error);
        } finally {
          if (this.quantityController === controller) {
            this.quantityController = null;
          }
        }
      }

                  updatePrice() {
        this.findMainProductInfo();

        const sourcePrice = this.mainProductInfo?.querySelector(
          '.f-price'
        );

        const stickyPrice = this.querySelector(
          '.sticky-atc-bar__product-info .f-price'
        );

        if (!sourcePrice || !stickyPrice) {
          return;
        }

        if (stickyPrice.innerHTML !== sourcePrice.innerHTML) {
          stickyPrice.innerHTML = sourcePrice.innerHTML;
        }

        [
          'f-price--sold-out',
          'f-price--on-sale',
          'f-price--no-compare',
          'volume-pricing--sale-badge',
        ].forEach((className) => {
          stickyPrice.classList.toggle(
            className,
            sourcePrice.classList.contains(className)
          );
        });
      }



      onSubmit(event) {
        this.findMainProductInfo();

        const hasRequiredFields = Boolean(
          this.mainProductInfo?.querySelector('[data-custom-property]')
        );

        if (!this.hasLaunch && !hasRequiredFields) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        if (this.hasLaunch) {
          /*
           * Ricontrolla la fase all'invio, senza aspettare
           * il prossimo intervallo di sincronizzazione.
           */
          this.mainLaunchView?.updatePhase();
          this.syncLaunchState();
        }

        const mainForm = this.productFormActions;
        const mainButton = mainForm?.querySelector('[name="add"]');
        const mainInput = mainForm?.querySelector('[name="id"]');

        if (
          this.pendingSelection ||
          this.mainProductInfo?.productInfoRequestPending ||
          !mainForm ||
          !mainButton ||
          mainButton.disabled ||
          !mainInput ||
          mainInput.disabled ||
          String(mainInput.value) !== String(this.select?.value) ||
          (
            this.hasLaunch &&
            this.mainLaunchView?.getAttribute('data-visible-phase') !==
              'in_vendita'
          )
        ) {
          return;
        }

        const mainQuantity = mainForm.querySelector('[name="quantity"]');
        const stickyQuantity = this.quantityInput;

        if (mainQuantity && stickyQuantity) {
          this.syncQuantityFromMain(false);
          mainQuantity.value = stickyQuantity.value;
          mainQuantity.dispatchEvent(
            new Event('change', { bubbles: true })
          );
        }

        if (!mainForm.reportValidity()) {
          mainForm.scrollIntoView({
            behavior: FoxTheme.config.disableAnimations ? 'auto' : 'smooth',
            block: 'center',
          });
          return;
        }

        mainForm.requestSubmit(mainButton);
      }
    }
  );
}
